import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Card, Chip, Container, IconButton, ListItem, ListItemAvatar, ListItemButton, ListItemText, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography, styled } from "@mui/material";
import { useLocation, useParams } from 'react-router-dom';
import { Notification } from '../../types/Notification';
import { DateTime } from 'luxon';
import { Timestamp, doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import passportRequestConverter from '../../converters/passportRequestConverter';
import { PassportRequest } from '../../types/PassportRequest';
import { Employee } from '../../types/Employee';
import { queryEmployee } from '../../utils/queryEmployee';
import { ChatBubbleRounded } from '@mui/icons-material';
import { NotificationPassportRequest } from './NotificationPassportRequest';
import { useAppSelector } from '../../store';
import notificationConverter from '../../converters/notificationConverter';
import { NotificationMedicalPaperRequest } from './NotificationMedicalPaperRequest';
import { NotificationUmrahPaperRequest } from './NotificationUmrahPaperRequest';
import { NotificationUniformRequest } from './NotificationUniformRequest';
import { useSnackbar } from 'notistack';
import { UmrahPaperRequest } from '../../types/UmrahPaperRequest';
import { RequestActionBox } from '../RequestActionBox';
import { UmrahPaperRequestInfoBox } from '../dashboard/umrah/UmrahPaperRequestInfoBox';
import { NotificationEmployeeHeading } from './NotificationEmployeeHeading';
import { ReceiptRequest } from '../../types/ReceiptRequest';
import receiptRequestConverter from '../../converters/receiptRequestConverter';
import { EnhancedTableCell } from '../EnhancedTableCell';
import { ChipColor, ChipIcon } from '../ChipUtils';

const ReceiptRequestNotification = (props: { notification: Notification }) => {
  const { notification } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [title, setTitle] = useState('Iqama Renewal Receipt');

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [receiptRequest, setReceiptRequest] = useState<ReceiptRequest>();
  const [employee, setEmployee] = useState<Employee>();

  const db = getFirestore();

  useEffect(() => {
    if (receiptRequest?.idNumber !== employee?.idNumber) {
      setLoading(true);
      if (receiptRequest !== undefined) {
        if (receiptRequest.type === 'Iqama')
          setTitle('Iqama Renewal Receipt');
        else if (receiptRequest.type === 'Vacation')
          setTitle('Tashera Payments');
        else
          setTitle('Other Files');

        queryEmployee(receiptRequest.idNumber).then(e => {
          setEmployee(e);
          setLoading(false);
        });
      }
    }
  }, [receiptRequest]);
  
  useEffect(() => {
    if (notification.pathToDocument) {
      getDoc(doc(db, notification.pathToDocument).withConverter(receiptRequestConverter)).then(qDocSnapshot => {
        if (qDocSnapshot.exists()) {
          setReceiptRequest(qDocSnapshot.data());
        }
      });
    }
  }, []);

  return (
    <Box>
      <Typography variant='h4' mb={1}>{title}</Typography>
      <Card>
        {loading ? (
          <Box p={1}>
            <Typography>Please wait...</Typography>
          </Box>
        ) : (
          <Box p={1}>
            <Stack direction='row' mb={2}>
              {employee && <NotificationEmployeeHeading employee={employee} />}
            </Stack>
            {receiptRequest && (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <EnhancedTableCell width={250}>Date Requested</EnhancedTableCell>
                      <TableCell>{DateTime.fromISO(receiptRequest.dateRequested).toFormat('MMMM dd, yyyy — hh:mm:ss a')}</TableCell>
                    </TableRow>
                    <TableRow sx={{
                      '&:last-child td, &:last-child th': { border: 0 } 
                    }}>
                      <EnhancedTableCell width={250}>Status</EnhancedTableCell>
                      <TableCell>
                        <Chip icon={ChipIcon(receiptRequest.status)} label={receiptRequest.status} color={ChipColor(receiptRequest.status)} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export const NotificationPage = () => {
  const { state } = useLocation();
  const { id } = useParams();
  const authState = useAppSelector(s => s.auth);

  const adminRoles = ['super', 'admin'];
  const adminOnly = authState.URB && authState.URB.roles.some((r: string) => adminRoles.includes(r));

  const [notification, setNotification] = useState<Notification | undefined>(state ? state as Notification : undefined);
  const [type, setType] = useState<{type: string, subType: string}>();
  
  const Content = useMemo(() => {
    return () => {
      if (notification) {
        if (type?.type === 'Employees') {
          if (type.subType === 'Vacations') {
            
          } else if (type.subType === 'ReceiptRequests') {
            return (<ReceiptRequestNotification notification={notification} />);
          } else if (type.subType === 'PassportRequests') {
            return (<NotificationPassportRequest notification={notification} />);
          } else if (type.subType === 'MedicalPaperRequests') {
            return (<NotificationMedicalPaperRequest notification={notification} />);
          } else if (type.subType === 'UmrahPaperRequests') {
            return (<NotificationUmrahPaperRequest notification={notification} />);
          } else if (type.subType === 'UniformRequests') {
            return (<NotificationUniformRequest notification={notification} />);
          }
        }
      }
  
      return (
        <Typography>Notification</Typography>
      );
    }
  }, [type, notification]);

  const markAsRead = () => {
    if (notification?.status === 'Unread') {
      const db = getFirestore();
      const d = doc(db, `Employees/${authState.idNumber}/Notifications/${id}`).withConverter(notificationConverter);
      setDoc(d, { status: 'Read', dateRead: DateTime.now().toISO(), lastUpdate: Timestamp.now() }, { merge: true });
    }
  };

  useEffect(() => {
    if (Boolean(state)) {
      setNotification(state as Notification);
    } else {
      // Query from firestore database
    }
  }, [id]);

  useEffect(() => {
    if (notification) {
      if (notification.pathToDocument !== undefined) {
        const segments = notification.pathToDocument.split('/');
        if (segments.length >= 3 && segments[0] === 'Employees') {
          setType({
            type: 'Employees',
            subType: segments[2],
          });
        }
      }

      markAsRead();
    }
  }, [notification]);

  return (
    <Container disableGutters>
      {notification && (
        <>
          <Paper sx={{ p: 1, mb: 3 }}>
            <Box mb={2}>
              <Typography variant='h6'>{notification.title}</Typography>
              <Typography variant='caption'>{DateTime.fromISO(notification.dateCreated).toFormat('MMMM dd, yyyy hh:mm:ss a')}</Typography>
            </Box>
            <Box>
              <Typography variant='body1'>{notification.body}</Typography>
            </Box>
          </Paper>
          {adminOnly && (
            <Content />
          )}
        </>
      )}
    </Container>
  );
};

